// components/common/AppSidebar.js
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { showToast } from "../../store/uiSlice";
import { setBalance } from "../../store/tradeSlice";
import { updateUser } from "../../store/authSlice";
import { fmtUSD } from "../../utils/formatters";
import api from "../../services/api";
import depositIcon from "../../assets/img/svg/deposit.svg";
import withdrawIcon from "../../assets/img/svg/withdraw.svg";
import documentsIcon from "../../assets/img/svg/doucments.svg";
import quizIcon from "../../assets/img/svg/quiz.svg";
import accountIcon from "../../assets/img/svg/account.svg";
import logoutIcon from "../../assets/img/svg/logout.svg";
import logoWhite from "../../assets/img/logo/logo-white.svg";

// UPI per-transaction cap (mirrors backend enforcement)
const UPI_MAX_WITHDRAWAL = 200;

const QUESTIONS = [
  {
    page: "1/6",
    question: "Main sources of funds available for trading",
    type: "multiple_choice",
    options: [
      "Employment / Business Income",
      "Savings and Investments",
      "Retirement Income",
      "Company Profit/Dividends",
      "Inheritance",
      "Property Sale",
      "Loan/ Borrowings/Gifts",
      "Other",
    ],
  },
  {
    page: "2/6",
    question:
      "What is the amount that you expect to deposit in the next 12 months? (USD)",
    type: "multiple_choice",
    options: [
      "0 - 20,000",
      "20,001 - 50,000",
      "50,001 - 250,000",
      "250,001 - 500,000",
      "500,001 - 1,000,000",
      "More than 1,000,000",
    ],
  },
  {
    page: "3/6",
    question: "Your total Annual Estimated Income (USD)",
    type: "multiple_choice",
    options: [
      "Less than 50,000",
      "50,001 - 100,000",
      "100,001 - 500,000",
      "500,001 - 1,000,000",
      "More than 1,000,000",
    ],
  },
  {
    page: "4/6",
    question:
      'Are you a Politically Exposed Person ("PEP") or a Family Member of a Close Associate of a PEP?',
    type: "yes_no",
    options: ["No", "Yes"],
  },
  {
    page: "5/6",
    question:
      "What is the nature of your profession/business or relevant former profession/business?",
    type: "multiple_choice",
    options: [
      "Accounting and Finance Services",
      "Company Service Providers",
      "Real Estate",
      "Pharmaceuticals and Healthcare",
      "Arms Trade and Defence",
      "Casinos",
      "Banking",
      "Financial Services",
      "Computer Software, Internet, IT",
      "Government Activities",
      "Mathematics",
      "Education",
      "Engineering and/or Construction",
      "Other",
    ],
  },
  {
    page: "6/6",
    question:
      "Do you understand that trading on CFDs carries significant risk of loss?",
    type: "confirmation",
    options: [
      "Yes, I confirm I understand that trading in CFDs carries significant risk of loss.",
    ],
  },
];

// ─── DEPOSIT METHODS (sequence: Bank Transfer → AnzoTrade Pay → UPI → UPI 1 → UPI 2) ───
const DEPOSIT_METHODS = [
  {
    key: "bank_transfer",
    label: "Bank Transfer",
    icon: "fa-building-columns",
    color: "#3b82f6",
  },
  {
    key: "payalma",
    label: "AnzoTrade Pay",
    icon: "fa-wallet",
    color: "#f59e0b",
  },
  { key: "wipays_upi", label: "UPI", icon: "fa-qrcode", color: "#10b981" },
  {
    key: "upi1",
    label: "UPI 1",
    icon: "fa-mobile-screen",
    color: "#8b5cf6",
  },
  {
    key: "custom_upi",
    label: "UPI 2",
    icon: "fa-mobile-screen",
    color: "#f59e0b",
  },
];

// UPI configurations for each tab
const UPI_CONFIG = {
  upi1: {
    label: "UPI 1",
    upiId: "rithe304@okhdfcbank",
    color: "#8b5cf6",
  },
  custom_upi: {
    label: "UPI 2",
    upiId: "-----",
    color: "#f59e0b",
  },
};

const AppSidebar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { logout } = useAuth();
  const { user } = useSelector((state) => state.auth);
  const { balance } = useSelector((state) => state.trade);
  const [activePopup, setActivePopup] = useState(null);

  const [depositAmount, setDepositAmount] = useState("");
  const [depositMethod, setDepositMethod] = useState("bank_transfer");
  const [isDepositing, setIsDepositing] = useState(false);
  const [depositHistory, setDepositHistory] = useState([]);
  const [bankScreenshot, setBankScreenshot] = useState(null);
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [customUpiScreenshot, setCustomUpiScreenshot] = useState(null);
  const [showCustomUpiDetails, setShowCustomUpiDetails] = useState(false);
  const [utrNumber, setUtrNumber] = useState("");
  const [paymentNote, setPaymentNote] = useState("");

  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawMethod, setWithdrawMethod] = useState("payalma");
  const [payalmaDetails, setPayalmaDetails] = useState({
    accountName: "",
    accountNumber: "",
    bankName: "",
    bankCode: "",
  });
  const [cryptoDetails, setCryptoDetails] = useState({
    walletAddress: "",
    network: "ERC20",
  });
  const [ewalletDetails, setEwalletDetails] = useState({
    email: "",
    provider: "PayPal",
  });
  // UPI withdrawal details
  const [upiDetails, setUpiDetails] = useState({
    upiId: "",
    accountName: "",
  });
  const [isWithdrawing, setIsWithdrawing] = useState(false);

  const [isUploading, setIsUploading] = useState(false);
  const [isSubmittingDocs, setIsSubmittingDocs] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({
    id: "not_uploaded",
    address: "not_uploaded",
    bank: "not_uploaded",
    selfie: "not_uploaded",
  });
  const [verificationStatus, setVerificationStatus] = useState({
    isVerified: false,
    idVerified: false,
    addressVerified: false,
    selfieVerified: false,
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmittingQuestionnaire, setIsSubmittingQuestionnaire] =
    useState(false);

  const isKycComplete =
    verificationStatus.isVerified ||
    (verificationStatus.idVerified &&
      verificationStatus.addressVerified &&
      verificationStatus.selfieVerified);
  const isQuestionnaireComplete = user?.questionnaireComplete === true;

  const menuItems = [
    {
      key: "deposit",
      label: "Deposit",
      icon: depositIcon,
      color: "#10b981",
      bgColor: "rgba(16, 185, 129, 0.12)",
    },
    {
      key: "withdrawal",
      label: "Withdraw",
      icon: withdrawIcon,
      color: "#ef4444",
      bgColor: "rgba(239, 68, 68, 0.12)",
    },
    {
      key: "documents",
      label: "KYC",
      icon: documentsIcon,
      color: "#8b5cf6",
      bgColor: "rgba(139, 92, 246, 0.12)",
      completed: isKycComplete,
    },
    {
      key: "questionnaire",
      label: "Quiz",
      icon: quizIcon,
      color: "#8b5cf6",
      bgColor: "rgba(139, 92, 246, 0.12)",
      completed: isQuestionnaireComplete,
    },
  ];

  useEffect(() => {
    if (activePopup === "deposit") fetchDepositHistory();
    if (activePopup === "documents") fetchVerificationStatus();
  }, [activePopup]);

  useEffect(() => {
    fetchVerificationStatus();
  }, []);

  const handleLogout = () => {
    setActivePopup(null);
    logout();
  };
  const openPopup = (key) => {
    setActivePopup(key);
    if (key === "questionnaire") {
      setCurrentStep(0);
      setAnswers({});
      if (user?.questionnaire) {
        try {
          const existing =
            typeof user.questionnaire === "string"
              ? JSON.parse(user.questionnaire)
              : user.questionnaire;
          setAnswers(existing);
        } catch (e) {}
      }
    }
  };

  const resetPopupStates = () => {
    setDepositAmount("");
    setWithdrawAmount("");
    setPayalmaDetails({
      accountName: "",
      accountNumber: "",
      bankName: "",
      bankCode: "",
    });
    setCryptoDetails({ walletAddress: "", network: "ERC20" });
    setUpiDetails({ upiId: "", accountName: "" });
    setCurrentStep(0);
    setAnswers({});
    setCustomUpiScreenshot(null);
    setUtrNumber("");
    setPaymentNote("");
  };

  const fetchDepositHistory = async () => {
    try {
      const response = await api.get("/deposit/history");
      const allDeposits = response.data || [];
      setDepositHistory(
        allDeposits.filter((d) => d.status === "completed").slice(0, 5),
      );
    } catch (error) {}
  };

  const processDeposit = async () => {
    const amountNum = parseFloat(depositAmount);
    const inr = ["wipays_upi", "upi1", "payalma", "custom_upi"].includes(
      depositMethod,
    );
    const minAmount = inr ? 100 : 50;
    if (!amountNum || amountNum < minAmount) {
      dispatch(
        showToast({
          message: `Minimum deposit is ${inr ? "₹100" : "$50"}`,
          type: "error",
        }),
      );
      return;
    }
    if (amountNum > 1000000) {
      dispatch(
        showToast({ message: "Maximum deposit is 1,000,000", type: "error" }),
      );
      return;
    }

    // ── UPI 1 (manual) ──
    if (depositMethod === "upi1") {
      if (!customUpiScreenshot) {
        dispatch(
          showToast({
            message: "Please upload a screenshot of your UPI payment",
            type: "error",
          }),
        );
        return;
      }
      setIsDepositing(true);
      try {
        const formData = new FormData();
        formData.append("amount", amountNum);
        formData.append("screenshot", customUpiScreenshot);
        if (utrNumber) formData.append("utrNumber", utrNumber);
        if (paymentNote) formData.append("paymentNote", paymentNote);
        formData.append("upiId", UPI_CONFIG.upi1.upiId);

        const response = await api.post("/deposit/custom-upi", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (response.data.success) {
          dispatch(
            showToast({
              message: "UPI 1 payment submitted! Admin will verify.",
              type: "success",
            }),
          );
          setDepositAmount("");
          setCustomUpiScreenshot(null);
          setUtrNumber("");
          setPaymentNote("");
          setShowCustomUpiDetails(false);
          setActivePopup(null);
          fetchDepositHistory();
        }
      } catch (error) {
        dispatch(
          showToast({
            message: error.response?.data?.message || "Deposit failed",
            type: "error",
          }),
        );
      } finally {
        setIsDepositing(false);
      }
      return;
    }

    // ── UPI 2 (manual) ──
    if (depositMethod === "custom_upi") {
      if (!customUpiScreenshot) {
        dispatch(
          showToast({
            message: "Please upload a screenshot of your UPI payment",
            type: "error",
          }),
        );
        return;
      }
      setIsDepositing(true);
      try {
        const formData = new FormData();
        formData.append("amount", amountNum);
        formData.append("screenshot", customUpiScreenshot);
        if (utrNumber) formData.append("utrNumber", utrNumber);
        if (paymentNote) formData.append("paymentNote", paymentNote);
        formData.append("upiId", UPI_CONFIG.custom_upi.upiId);

        const response = await api.post("/deposit/custom-upi", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (response.data.success) {
          dispatch(
            showToast({
              message: "UPI 2 payment submitted! Admin will verify.",
              type: "success",
            }),
          );
          setDepositAmount("");
          setCustomUpiScreenshot(null);
          setUtrNumber("");
          setPaymentNote("");
          setShowCustomUpiDetails(false);
          setActivePopup(null);
          fetchDepositHistory();
        }
      } catch (error) {
        dispatch(
          showToast({
            message: error.response?.data?.message || "Deposit failed",
            type: "error",
          }),
        );
      } finally {
        setIsDepositing(false);
      }
      return;
    }

    // ── Bank Transfer ──
    if (depositMethod === "bank_transfer") {
      if (!bankScreenshot) {
        dispatch(
          showToast({
            message: "Please upload a screenshot of your transfer",
            type: "error",
          }),
        );
        return;
      }
      setIsDepositing(true);
      try {
        const formData = new FormData();
        formData.append("amount", amountNum);
        formData.append("screenshot", bankScreenshot);
        const response = await api.post("/deposit/bank-transfer", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (response.data.success) {
          dispatch(
            showToast({
              message: "Bank transfer submitted! Admin will verify.",
              type: "success",
            }),
          );
          setDepositAmount("");
          setBankScreenshot(null);
          setShowBankDetails(false);
          setActivePopup(null);
          fetchDepositHistory();
        }
      } catch (error) {
        dispatch(
          showToast({
            message: error.response?.data?.message || "Deposit failed",
            type: "error",
          }),
        );
      } finally {
        setIsDepositing(false);
      }
      return;
    }

    // ── Gateway methods (wipays_upi, payalma) ──
    setIsDepositing(true);
    try {
      let endpoint;
      if (depositMethod === "wipays_upi") endpoint = "/deposit/wipays/upi";
      else endpoint = "/deposit/payalma";
      const response = await api.post(endpoint, { amount: amountNum });
      if (response.data.success) {
        if (depositMethod === "wipays_upi") {
          dispatch(
            showToast({ message: "UPI payment initiated", type: "success" }),
          );
        } else {
          window.location.href = response.data.checkoutUrl;
        }
      }
    } catch (error) {
      dispatch(
        showToast({
          message: error.response?.data?.message || "Deposit failed",
          type: "error",
        }),
      );
    } finally {
      setIsDepositing(false);
    }
  };

  const copyUpiId = (upiId) => {
    navigator.clipboard.writeText(upiId).then(() => {
      dispatch(showToast({ message: "UPI ID copied!", type: "success" }));
    });
  };

  const validateWithdrawal = () => {
    const amountNum = parseFloat(withdrawAmount);
    if (!amountNum || amountNum < 5) {
      dispatch(
        showToast({ message: "Minimum withdrawal is $5.00", type: "error" }),
      );
      return false;
    }
    if (amountNum > balance) {
      dispatch(
        showToast({
          message: `Insufficient balance. Max: ${fmtUSD(balance)}`,
          type: "error",
        }),
      );
      return false;
    }
    // UPI-specific $200 per-transaction cap
    if (withdrawMethod === "upi" && amountNum > UPI_MAX_WITHDRAWAL) {
      dispatch(
        showToast({
          message: `UPI withdrawals are limited to $${UPI_MAX_WITHDRAWAL.toFixed(2)} per transaction`,
          type: "error",
        }),
      );
      return false;
    }
    if (withdrawMethod === "payalma") {
      if (!payalmaDetails.accountName?.trim()) {
        dispatch(
          showToast({ message: "Account holder name required", type: "error" }),
        );
        return false;
      }
      if (!payalmaDetails.accountNumber?.trim()) {
        dispatch(
          showToast({ message: "Account number required", type: "error" }),
        );
        return false;
      }
      if (!payalmaDetails.bankName?.trim()) {
        dispatch(showToast({ message: "Bank name required", type: "error" }));
        return false;
      }
      if (!payalmaDetails.bankCode?.trim()) {
        dispatch(
          showToast({ message: "Bank code / IFSC required", type: "error" }),
        );
        return false;
      }
    }
    if (withdrawMethod === "upi") {
      if (!upiDetails.upiId?.trim()) {
        dispatch(showToast({ message: "UPI ID required", type: "error" }));
        return false;
      }
      if (!/^[\w.\-]{2,256}@[a-zA-Z]{2,64}$/.test(upiDetails.upiId.trim())) {
        dispatch(
          showToast({
            message: "Enter a valid UPI ID (e.g. name@bank)",
            type: "error",
          }),
        );
        return false;
      }
    }
    if (withdrawMethod === "crypto" && !cryptoDetails.walletAddress?.trim()) {
      dispatch(
        showToast({ message: "Wallet address required", type: "error" }),
      );
      return false;
    }
    if (withdrawMethod === "ewallet" && !ewalletDetails.email?.trim()) {
      dispatch(showToast({ message: "Wallet email required", type: "error" }));
      return false;
    }
    return true;
  };

  const processWithdrawal = async () => {
    if (!validateWithdrawal()) return;
    setIsWithdrawing(true);
    try {
      let methodDetails;
      if (withdrawMethod === "payalma") {
        methodDetails = payalmaDetails;
      } else if (withdrawMethod === "upi") {
        methodDetails = {
          upiId: upiDetails.upiId.trim(),
          accountName: upiDetails.accountName.trim(),
        };
      } else if (withdrawMethod === "crypto") {
        methodDetails = cryptoDetails;
      } else {
        methodDetails = ewalletDetails;
      }
      const response = await api.post("/withdrawal", {
        amount: parseFloat(withdrawAmount),
        method: withdrawMethod,
        methodDetails,
      });
      if (response.data.success) {
        const balanceResponse = await api.get("/user/balance");
        dispatch(setBalance(balanceResponse.data.balance));
        dispatch(
          showToast({
            message: "Withdrawal request submitted for review!",
            type: "success",
          }),
        );
        setWithdrawAmount("");
        setActivePopup(null);
        resetPopupStates();
      }
    } catch (error) {
      dispatch(
        showToast({
          message: error.response?.data?.message || "Withdrawal failed",
          type: "error",
        }),
      );
    } finally {
      setIsWithdrawing(false);
    }
  };

  const fetchVerificationStatus = async () => {
    try {
      const response = await api.get("/documents/verification/status");
      const data = response.data;
      setVerificationStatus({
        isVerified: data.isVerified || false,
        idVerified: data.idVerified || false,
        addressVerified: data.addressVerified || false,
        selfieVerified: data.selfieVerified || false,
      });
      if (data.documents && Array.isArray(data.documents)) {
        const statusMap = { ...uploadStatus };
        data.documents.forEach((doc) => {
          statusMap[doc.type] = doc.status || "not_uploaded";
        });
        setUploadStatus(statusMap);
      }
    } catch (error) {}
  };

  const handleFileUpload = async (type, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("type", type);
    formData.append("document", file);
    setIsUploading(true);
    try {
      await api.post("/documents/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadStatus((prev) => ({ ...prev, [type]: "uploaded" }));
      dispatch(showToast({ message: "Document uploaded!", type: "success" }));
      fetchVerificationStatus();
    } catch (error) {
      dispatch(showToast({ message: "Upload failed", type: "error" }));
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmitVerification = async () => {
    const requiredTypes = ["id", "address", "selfie"];
    const uploadedCount = requiredTypes.filter(
      (t) => uploadStatus[t] === "uploaded" || uploadStatus[t] === "pending",
    ).length;
    if (uploadedCount < requiredTypes.length) {
      dispatch(
        showToast({
          message: `Upload all required documents (${uploadedCount}/${requiredTypes.length})`,
          type: "error",
        }),
      );
      return;
    }
    setIsSubmittingDocs(true);
    try {
      await api.post("/documents/submit");
      dispatch(
        showToast({
          message: "Documents submitted for verification!",
          type: "success",
        }),
      );
      fetchVerificationStatus();
    } catch (error) {
      dispatch(showToast({ message: "Submission failed", type: "error" }));
    } finally {
      setIsSubmittingDocs(false);
    }
  };

  const handleQuestionnaireSubmit = async () => {
    setIsSubmittingQuestionnaire(true);
    try {
      const response = await api.post("/user/questionnaire", { answers });
      if (response.data.success) {
        dispatch(
          updateUser({
            questionnaire: answers,
            questionnaireComplete: response.data.questionnaireComplete,
          }),
        );
        dispatch(
          showToast({ message: "Questionnaire completed!", type: "success" }),
        );
        setActivePopup(null);
      }
    } catch (error) {
      dispatch(showToast({ message: "Failed to save", type: "error" }));
    } finally {
      setIsSubmittingQuestionnaire(false);
    }
  };

  const getPopupTitle = () => {
    switch (activePopup) {
      case "deposit":
        return "Deposit Funds";
      case "withdrawal":
        return "Withdraw Funds";
      case "documents":
        return isKycComplete ? "KYC Completed" : "KYC Documents";
      case "questionnaire":
        return isQuestionnaireComplete
          ? "Questionnaire Completed"
          : "Trading Questionnaire";
      default:
        return "";
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
    fontSize: "13px",
    outline: "none",
    color: "#1f2937",
    background: "#ffffff",
    transition: "border-color 0.2s",
    boxSizing: "border-box",
  };
  const labelStyle = {
    fontSize: "10px",
    fontWeight: "600",
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "5px",
    display: "block",
  };

  // ─── DEPOSIT CONTENT with TABS ───
  const renderDepositContent = () => {
    // Show UPI details for the currently selected UPI method
    const activeUpiConfig = UPI_CONFIG[depositMethod];

    return (
      <div className="p-5">
        <div
          className="mb-4 p-3 rounded-lg text-center"
          style={{ background: "#f9fafb", border: "1px solid #e5e7eb" }}
        >
          <div className="text-[10px] font-semibold text-gray-500 uppercase mb-1">
            Available Balance
          </div>
          <div className="text-2xl font-extrabold text-gray-900">
            {fmtUSD(balance)}
          </div>
        </div>

        {/* Method Tabs - 5 methods grid */}
        <div className="mb-4">
          <label style={labelStyle}>Payment Method</label>
          <div className="grid grid-cols-5 gap-2">
            {DEPOSIT_METHODS.map((method) => (
              <button
                key={method.key}
                onClick={() => {
                  setDepositMethod(method.key);
                  if (method.key === "bank_transfer") {
                    setShowBankDetails(true);
                    setShowCustomUpiDetails(false);
                  } else if (method.key === "upi1") {
                    setShowCustomUpiDetails(true);
                    setShowBankDetails(false);
                  } else if (method.key === "custom_upi") {
                    setShowCustomUpiDetails(true);
                    setShowBankDetails(false);
                  } else {
                    setShowBankDetails(false);
                    setShowCustomUpiDetails(false);
                  }
                }}
                className="flex flex-col items-center gap-2 py-3 px-1 rounded-xl transition-all border-2"
                style={{
                  borderColor:
                    depositMethod === method.key ? method.color : "#e5e7eb",
                  background:
                    depositMethod === method.key
                      ? `${method.color}15`
                      : "#ffffff",
                  cursor: "pointer",
                }}
              >
                <i
                  className={`fas ${method.icon}`}
                  style={{
                    fontSize: 16,
                    color:
                      depositMethod === method.key ? method.color : "#9ca3af",
                  }}
                />
                <span
                  className="text-[9px] font-bold text-center leading-tight"
                  style={{
                    color:
                      depositMethod === method.key ? method.color : "#6b7280",
                  }}
                >
                  {method.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* UPI Details for UPI 1 or UPI 2 */}
        {activeUpiConfig && (
          <div
            className="mb-3 p-3 rounded-lg"
            style={{ background: "#f9fafb", border: "1px solid #e5e7eb" }}
          >
            <div className="text-[10px] font-bold text-gray-700 uppercase mb-2">
              {activeUpiConfig.label} Payment Details
            </div>
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between items-center">
                <span className="text-gray-500">UPI ID</span>
                <span className="font-semibold text-gray-900">
                  {activeUpiConfig.upiId}
                </span>
              </div>
            </div>
            <button
              onClick={() => copyUpiId(activeUpiConfig.upiId)}
              className="mt-2 w-full py-2 rounded-lg text-xs font-bold transition-all"
              style={{
                background: activeUpiConfig.color,
                color: "#ffffff",
                border: "none",
                cursor: "pointer",
              }}
            >
              <i className="fas fa-copy mr-1" /> Copy UPI ID
            </button>
            <div className="text-[10px] text-yellow-600 mt-2">
              ⚠ Steps: 1. Open any UPI app → 2. Send payment to above UPI ID →
              3. Upload screenshot below
            </div>
          </div>
        )}

        {/* Bank Details for bank_transfer */}
        {showBankDetails && depositMethod === "bank_transfer" && (
          <div
            className="mb-3 p-3 rounded-lg"
            style={{ background: "#f9fafb", border: "1px solid #e5e7eb" }}
          >
            <div className="text-[10px] font-bold text-gray-700 uppercase mb-2">
              Bank Details
            </div>
            <div className="space-y-1.5 text-xs">
              {[
                ["Account Number", "104601000004133"],
                ["IFSC Code", "IOBA0001046"],
                ["Account Name", "R E BHAVITHRAN"],
                ["Bank", "Indian Overseas Bank"],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between">
                  <span className="text-gray-500">{label}</span>
                  <span className="font-semibold text-gray-900">{value}</span>
                </div>
              ))}
            </div>
            <div className="text-[10px] text-yellow-600 mt-2">
              ⚠ After transfer, upload screenshot below
            </div>
          </div>
        )}

        <div className="mb-3">
          <label style={labelStyle}>
            Amount ({["bank_transfer"].includes(depositMethod) ? "$" : "₹"})
          </label>
          <input
            type="number"
            value={depositAmount}
            onChange={(e) => setDepositAmount(e.target.value)}
            placeholder="0.00"
            style={inputStyle}
            onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
            onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
          />
        </div>

        {(depositMethod === "custom_upi" || depositMethod === "upi1") && (
          <div className="space-y-3 mb-4">
            <div>
              <label style={labelStyle}>UTR Number (Optional)</label>
              <input
                type="text"
                value={utrNumber}
                onChange={(e) => setUtrNumber(e.target.value)}
                placeholder="Enter UTR/Reference number"
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
              />
            </div>
            <div>
              <label style={labelStyle}>Payment Note (Optional)</label>
              <input
                type="text"
                value={paymentNote}
                onChange={(e) => setPaymentNote(e.target.value)}
                placeholder="Any additional information"
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
              />
            </div>
            <div>
              <label style={labelStyle}>Payment Screenshot *</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setCustomUpiScreenshot(e.target.files[0])}
                className="w-full text-xs"
                style={{ color: "#6b7280" }}
              />
              {customUpiScreenshot && (
                <div className="text-xs text-green-600 mt-1">
                  ✓ {customUpiScreenshot.name}
                </div>
              )}
            </div>
          </div>
        )}

        {depositMethod === "bank_transfer" && (
          <div className="mb-4">
            <label style={labelStyle}>Transfer Screenshot *</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setBankScreenshot(e.target.files[0])}
              className="w-full text-xs"
              style={{ color: "#6b7280" }}
            />
            {bankScreenshot && (
              <div className="text-xs text-green-600 mt-1">
                ✓ {bankScreenshot.name}
              </div>
            )}
          </div>
        )}

        {/* Preset amounts: 5000, 10000, 15000, 20000 */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[5000, 10000, 15000, 20000].map((amt) => (
            <button
              key={amt}
              onClick={() => setDepositAmount(String(amt))}
              className="py-2 rounded-lg text-xs font-semibold transition-all"
              style={{
                background:
                  depositAmount === String(amt) ? "#8b5cf6" : "#f9fafb",
                color: depositAmount === String(amt) ? "#ffffff" : "#374151",
                border: "1px solid #e5e7eb",
                cursor: "pointer",
              }}
            >
              {["bank_transfer"].includes(depositMethod) ? "$" : "₹"}
              {amt.toLocaleString()}
            </button>
          ))}
        </div>

        <button
          onClick={processDeposit}
          disabled={isDepositing}
          className="w-full py-3 rounded-lg text-sm font-bold transition-all"
          style={{
            background: "#8b5cf6",
            color: "#ffffff",
            border: "none",
            cursor: "pointer",
            opacity: isDepositing ? 0.5 : 1,
          }}
        >
          {isDepositing
            ? "Processing..."
            : depositMethod === "bank_transfer"
              ? "Submit Bank Transfer"
              : depositMethod === "custom_upi"
                ? "Submit UPI 2 Payment"
                : depositMethod === "upi1"
                  ? "Submit UPI 1 Payment"
                  : "Proceed to Payment"}
        </button>
      </div>
    );
  };

  // ─── WITHDRAWAL CONTENT ───
  const renderWithdrawalContent = () => (
    <div className="p-5">
      <div
        className="mb-4 p-3 rounded-lg text-center"
        style={{ background: "#f9fafb", border: "1px solid #e5e7eb" }}
      >
        <div className="text-[10px] font-semibold text-gray-500 uppercase mb-1">
          Available Balance
        </div>
        <div className="text-2xl font-extrabold text-gray-900">
          {fmtUSD(balance)}
        </div>
      </div>
      <div className="mb-3">
        <label style={labelStyle}>Amount (USD)</label>
        <input
          type="number"
          value={withdrawAmount}
          onChange={(e) => setWithdrawAmount(e.target.value)}
          placeholder="0.00"
          style={inputStyle}
          onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
          onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
        />
        {withdrawMethod === "upi" && (
          <div className="text-[10px] text-yellow-600 mt-1 font-semibold">
            <i className="fas fa-circle-info mr-1" />
            Max ${UPI_MAX_WITHDRAWAL.toFixed(2)} per UPI withdrawal
          </div>
        )}
      </div>
      <div className="mb-3">
        <label style={labelStyle}>Method</label>
        <select
          value={withdrawMethod}
          onChange={(e) => setWithdrawMethod(e.target.value)}
          style={inputStyle}
        >
          <option value="payalma">Bank Transfer</option>
          <option value="upi">UPI</option>
          <option value="crypto">Cryptocurrency</option>
          <option value="ewallet">E-Wallet</option>
        </select>
      </div>
      {withdrawMethod === "payalma" && (
        <div className="space-y-2.5 mb-4">
          {[
            {
              label: "Account Holder Name",
              value: payalmaDetails.accountName,
              setter: (v) =>
                setPayalmaDetails({ ...payalmaDetails, accountName: v }),
            },
            {
              label: "Account Number",
              value: payalmaDetails.accountNumber,
              setter: (v) =>
                setPayalmaDetails({ ...payalmaDetails, accountNumber: v }),
            },
            {
              label: "Bank Name",
              value: payalmaDetails.bankName,
              setter: (v) =>
                setPayalmaDetails({ ...payalmaDetails, bankName: v }),
            },
            {
              label: "Bank Code / IFSC",
              value: payalmaDetails.bankCode,
              setter: (v) =>
                setPayalmaDetails({ ...payalmaDetails, bankCode: v }),
            },
          ].map((field) => (
            <div key={field.label}>
              <label style={labelStyle}>{field.label} *</label>
              <input
                type="text"
                value={field.value}
                onChange={(e) => field.setter(e.target.value)}
                placeholder={`Enter ${field.label.toLowerCase()}`}
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
              />
            </div>
          ))}
        </div>
      )}
      {withdrawMethod === "upi" && (
        <div className="space-y-2.5 mb-4">
          <div>
            <label style={labelStyle}>UPI ID *</label>
            <input
              type="text"
              value={upiDetails.upiId}
              onChange={(e) =>
                setUpiDetails({ ...upiDetails, upiId: e.target.value })
              }
              placeholder="yourname@bank"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
            />
          </div>
          <div>
            <label style={labelStyle}>Account Holder Name (Optional)</label>
            <input
              type="text"
              value={upiDetails.accountName}
              onChange={(e) =>
                setUpiDetails({ ...upiDetails, accountName: e.target.value })
              }
              placeholder="Name registered with UPI"
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
            />
          </div>
        </div>
      )}
      {withdrawMethod === "crypto" && (
        <div className="space-y-2.5 mb-4">
          <div>
            <label style={labelStyle}>Network</label>
            <select
              value={cryptoDetails.network}
              onChange={(e) =>
                setCryptoDetails({ ...cryptoDetails, network: e.target.value })
              }
              style={inputStyle}
            >
              <option>ERC20 (Ethereum)</option>
              <option>BEP20 (BSC)</option>
              <option>TRC20 (TRON)</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Wallet Address *</label>
            <input
              type="text"
              value={cryptoDetails.walletAddress}
              onChange={(e) =>
                setCryptoDetails({
                  ...cryptoDetails,
                  walletAddress: e.target.value,
                })
              }
              placeholder="Enter wallet address"
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
            />
          </div>
        </div>
      )}
      {withdrawMethod === "ewallet" && (
        <div className="space-y-2.5 mb-4">
          <div>
            <label style={labelStyle}>Provider</label>
            <select
              value={ewalletDetails.provider}
              onChange={(e) =>
                setEwalletDetails({
                  ...ewalletDetails,
                  provider: e.target.value,
                })
              }
              style={inputStyle}
            >
              <option>PayPal</option>
              <option>Skrill</option>
              <option>Neteller</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Wallet Email / ID *</label>
            <input
              type="email"
              value={ewalletDetails.email}
              onChange={(e) =>
                setEwalletDetails({ ...ewalletDetails, email: e.target.value })
              }
              placeholder="your@email.com"
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
            />
          </div>
        </div>
      )}
      <button
        onClick={processWithdrawal}
        disabled={isWithdrawing}
        className="w-full py-3 rounded-lg text-sm font-bold transition-all"
        style={{
          background: "#ef4444",
          color: "#ffffff",
          border: "none",
          cursor: "pointer",
          opacity: isWithdrawing ? 0.5 : 1,
        }}
      >
        {isWithdrawing ? "Processing..." : "Request Withdrawal"}
      </button>
    </div>
  );

  // ─── KYC CONTENT ───
  const renderDocumentsContent = () => (
    <div className="p-5">
      {isKycComplete ? (
        <div className="text-center py-10">
          <div className="flex items-center justify-center mb-6">
            <div className="relative">
              <div
                className="w-24 h-24 rounded-full flex items-center justify-center check-circle"
                style={{
                  background: "#8b5cf6",
                  boxShadow: "0 0 30px rgba(139, 92, 246, 0.4)",
                }}
              >
                <span className="checkmark-animated">✓</span>
              </div>
              <div
                className="absolute inset-0 rounded-full pulse-ring"
                style={{ border: "2px solid #8b5cf6" }}
              ></div>
              <div
                className="absolute inset-0 rounded-full pulse-ring-delay"
                style={{ border: "2px solid #8b5cf6" }}
              ></div>
            </div>
          </div>
          <div className="text-lg font-extrabold text-gray-900 mb-1">
            Verification Complete!
          </div>
          <div className="text-sm text-gray-500">
            Your account is fully verified.
          </div>
        </div>
      ) : (
        <div>
          <div className="text-sm text-gray-600 mb-6 flex items-center gap-2">
            <i className="fas fa-info-circle text-[#8b5cf6]" />
            Upload required documents for KYC verification. Accepted: JPG, PNG,
            PDF (max 10MB).
          </div>
          <div className="grid grid-cols-2 gap-4 mb-6">
            {[
              {
                type: "id",
                name: "Government ID",
                required: true,
                icon: "fa-passport",
                desc: "Passport, Driver's License, or National ID",
              },
              {
                type: "address",
                name: "Proof of Address",
                required: true,
                icon: "fa-file-alt",
                desc: "Utility bill or bank statement",
              },
              {
                type: "bank",
                name: "Bank Statement",
                required: false,
                icon: "fa-bank",
                desc: "Recent bank statement (optional)",
              },
              {
                type: "selfie",
                name: "Selfie with ID",
                required: true,
                icon: "fa-camera",
                desc: "Selfie holding your ID",
              },
            ].map((doc) => {
              const status = uploadStatus[doc.type];
              const isUploaded =
                status === "uploaded" ||
                status === "pending" ||
                status === "approved";
              const isVerified =
                verificationStatus[doc.type + "Verified"] ||
                status === "approved";
              return (
                <div
                  key={doc.type}
                  className="rounded-2xl p-6 transition-all border-2"
                  style={{
                    borderColor: isVerified
                      ? "#8b5cf6"
                      : isUploaded
                        ? "#c4b5fd"
                        : "#e5e7eb",
                    background: isVerified ? "#f5f3ff" : "white",
                  }}
                >
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                    style={{
                      background: "rgba(139, 92, 246, 0.12)",
                      border: "2px solid #8b5cf6",
                    }}
                  >
                    <i className={`fas ${doc.icon} text-xl text-[#8b5cf6]`} />
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-extrabold text-gray-900 text-base">
                      {doc.name}
                    </h3>
                    {doc.required && (
                      <span className="text-red-500 font-bold">*</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mb-4">{doc.desc}</p>
                  <div className="mb-4">
                    {isVerified ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#8b5cf6]">
                        <i className="fas fa-check-circle" /> Verified
                      </span>
                    ) : isUploaded ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#8b5cf6]">
                        <i className="fas fa-check-circle" /> Uploaded
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-400">
                        <i className="fas fa-cloud-upload-alt" /> Not uploaded
                      </span>
                    )}
                  </div>
                  <input
                    type="file"
                    id={`appsidebar-upload-${doc.type}`}
                    accept=".jpg,.jpeg,.png,.pdf"
                    className="hidden"
                    onChange={(e) =>
                      handleFileUpload(doc.type, e.target.files[0])
                    }
                  />
                  <button
                    onClick={() =>
                      document
                        .getElementById(`appsidebar-upload-${doc.type}`)
                        .click()
                    }
                    disabled={isUploading || isVerified}
                    className="w-full py-3 rounded-lg text-sm font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    style={{
                      background: isVerified ? "#10b981" : "#8b5cf6",
                      color: "white",
                      borderBottom:
                        "3px solid " + (isVerified ? "#047857" : "#7c3aed"),
                      cursor:
                        isUploading || isVerified ? "not-allowed" : "pointer",
                    }}
                  >
                    {isVerified ? (
                      <>
                        <i className="fas fa-check" /> Verified
                      </>
                    ) : isUploaded ? (
                      <>
                        <i className="fas fa-sync-alt text-xs" /> Re-upload
                      </>
                    ) : (
                      <>
                        <i className="fas fa-upload text-xs" /> Upload Document
                      </>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
          <button
            onClick={handleSubmitVerification}
            disabled={isSubmittingDocs}
            className="w-full py-3 rounded-lg text-sm font-bold transition-all"
            style={{
              background: "#8b5cf6",
              color: "#ffffff",
              border: "none",
              cursor: "pointer",
              opacity: isSubmittingDocs ? 0.5 : 1,
            }}
          >
            {isSubmittingDocs ? "Submitting..." : "Submit for Verification"}
          </button>
        </div>
      )}
    </div>
  );

  // ─── QUESTIONNAIRE CONTENT ───
  const renderQuestionnaireContent = () => {
    if (isQuestionnaireComplete) {
      return (
        <div className="text-center py-10">
          <div className="flex items-center justify-center mb-6">
            <div className="relative">
              <div
                className="w-24 h-24 rounded-full flex items-center justify-center check-circle"
                style={{
                  background: "#8b5cf6",
                  boxShadow: "0 0 30px rgba(139, 92, 246, 0.4)",
                }}
              >
                <span className="checkmark-animated">✓</span>
              </div>
              <div
                className="absolute inset-0 rounded-full pulse-ring"
                style={{ border: "2px solid #8b5cf6" }}
              ></div>
              <div
                className="absolute inset-0 rounded-full pulse-ring-delay"
                style={{ border: "2px solid #8b5cf6" }}
              ></div>
            </div>
          </div>
          <div className="text-lg font-extrabold text-gray-900 mb-1">
            Questionnaire Completed!
          </div>
          <div className="text-sm text-gray-500">
            You have completed the trading questionnaire.
          </div>
        </div>
      );
    }
    const currentQuestion = QUESTIONS[currentStep];
    const isLastStep = currentStep === QUESTIONS.length - 1;
    const selectedAnswer = answers[currentQuestion?.page] || "";
    const progress = ((currentStep + 1) / QUESTIONS.length) * 100;
    return (
      <div className="p-5">
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500">
              Question {currentStep + 1} of {QUESTIONS.length}
            </span>
            <span className="text-xs font-bold text-[#8b5cf6]">
              {Math.round(progress)}%
            </span>
          </div>
          <div
            className="h-2 rounded-full overflow-hidden"
            style={{ background: "#f3f4f6" }}
          >
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{
                width: `${progress}%`,
                background: "linear-gradient(90deg, #8b5cf6, #a78bfa)",
              }}
            />
          </div>
          <div className="flex justify-between mt-3 px-1">
            {QUESTIONS.map((q, idx) => (
              <div
                key={q.page}
                className={`w-2.5 h-2.5 rounded-full transition-all ${idx < currentStep ? "bg-[#10b981]" : idx === currentStep ? "bg-[#8b5cf6]" : "bg-gray-200"}`}
              />
            ))}
          </div>
        </div>
        <div className="mb-4 flex items-center gap-2">
          <span
            className="text-xs px-3 py-1 rounded-full font-mono font-extrabold border-2"
            style={{
              borderColor: "#8b5cf6",
              color: "#8b5cf6",
              background: "rgba(139, 92, 246, 0.08)",
            }}
          >
            {currentQuestion?.page}
          </span>
          <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider">
            {currentQuestion?.type === "confirmation"
              ? "Confirmation"
              : currentQuestion?.type === "yes_no"
                ? "Yes / No"
                : "Select one option"}
          </span>
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-6">
          {currentQuestion?.question}
        </h3>
        <div className="space-y-3 mb-6 max-h-[250px] overflow-y-auto">
          {currentQuestion?.options.map((option, idx) => (
            <button
              key={idx}
              onClick={() =>
                setAnswers((prev) => ({
                  ...prev,
                  [currentQuestion.page]: option,
                }))
              }
              className={`w-full text-left px-5 py-4 rounded-xl text-sm transition-all border-2 flex items-center gap-4 ${selectedAnswer === option ? "bg-gradient-to-r from-[#8b5cf6] to-[#a78bfa] text-white border-transparent shadow-lg" : "bg-gray-50 text-gray-700 border-gray-200 hover:border-[#c4b5fd] hover:bg-white"}`}
            >
              <div
                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedAnswer === option ? "border-white bg-white/20" : "border-gray-300"}`}
              >
                {selectedAnswer === option && (
                  <i className="fas fa-check text-xs text-white" />
                )}
              </div>
              <span className="font-medium">{option}</span>
            </button>
          ))}
        </div>
        <div className="flex gap-3">
          {currentStep > 0 && (
            <button
              onClick={() => setCurrentStep((prev) => prev - 1)}
              className="flex-1 py-3 rounded-lg text-sm font-bold text-gray-600 border-2 border-gray-200 hover:border-[#8b5cf6] hover:text-[#8b5cf6] transition-all bg-white"
            >
              <i className="fas fa-arrow-left mr-2" /> Back
            </button>
          )}
          <button
            onClick={() => setActivePopup(null)}
            className="flex-1 py-3 rounded-lg text-sm font-bold text-gray-500 hover:text-gray-700 transition-all"
          >
            Skip for now
          </button>
          <button
            onClick={() => {
              if (!selectedAnswer) {
                dispatch(
                  showToast({
                    message: "Please select an answer",
                    type: "error",
                  }),
                );
                return;
              }
              if (isLastStep) {
                handleQuestionnaireSubmit();
              } else {
                setCurrentStep((prev) => prev + 1);
              }
            }}
            disabled={isSubmittingQuestionnaire}
            className="flex-1 py-3 rounded-lg text-sm font-bold text-white transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            style={{
              background: isLastStep
                ? "#10b981"
                : "linear-gradient(to right, #8b5cf6, #a78bfa)",
            }}
          >
            {isSubmittingQuestionnaire ? (
              <>
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{" "}
                Saving...
              </>
            ) : isLastStep ? (
              <>
                <i className="fas fa-check" /> Submit
              </>
            ) : (
              <>
                Next <i className="fas fa-arrow-right" />
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  const renderPopupContent = () => {
    switch (activePopup) {
      case "deposit":
        return renderDepositContent();
      case "withdrawal":
        return renderWithdrawalContent();
      case "documents":
        return renderDocumentsContent();
      case "questionnaire":
        return renderQuestionnaireContent();
      default:
        return null;
    }
  };

  return (
    <>
      {/* Full height white sidebar - 90px width */}
      <div
        className="flex-shrink-0 flex flex-col items-center overflow-hidden h-full"
        style={{
          width: "90px",
          background: "#ffffff",
          borderRight: "1px solid #e5e7eb",
          zIndex: 30,
        }}
      >
        {/* Logo */}
        <div
          className="flex items-center justify-center w-full py-6 border-b"
          style={{ borderColor: "#f3f4f6" }}
        >
          <img
            src={logoWhite}
            alt="Logo"
            className="w-[100px] h-[70px] object-contain"
          />
        </div>

        {/* Menu Items */}
        <div className="flex-1 flex flex-col items-center gap-2.5 py-4 w-full">
          {menuItems.map((item) => (
            <button
              key={item.key}
              onClick={() => openPopup(item.key)}
              className="w-full flex flex-col items-center gap-2 py-3.5 px-2 transition-all relative group"
              style={{
                border: "none",
                background:
                  activePopup === item.key ? item.bgColor : "transparent",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                if (activePopup !== item.key)
                  e.currentTarget.style.background = item.bgColor;
              }}
              onMouseLeave={(e) => {
                if (activePopup !== item.key)
                  e.currentTarget.style.background = "transparent";
              }}
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center transition-all group-hover:scale-110"
                style={{ background: item.bgColor }}
              >
                <img
                  src={item.icon}
                  alt={item.label}
                  className="w-7 h-7"
                  style={{ filter: "brightness(0)" }}
                />
              </div>
              <span
                className="text-[9px] font-syne font-bold tracking-wide"
                style={{ color: item.completed ? "#10b981" : item.color }}
              >
                {item.label}
              </span>
            </button>
          ))}
        </div>

        {/* Bottom - Account + Logout */}
        <div
          className="flex flex-col items-center gap-2 py-4 w-full border-t"
          style={{ borderColor: "#f3f4f6" }}
        >
          <button
            onClick={() => navigate("/profile")}
            className="w-full flex flex-col items-center gap-2 py-3.5 px-2 transition-all"
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
            }}
            title={`${user?.firstName || "User"} ${user?.lastName || ""}`}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(139, 92, 246, 0.08)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
          >
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center"
              style={{ background: "rgba(139, 92, 246, 0.12)" }}
            >
              <img
                src={accountIcon}
                alt="Account"
                className="w-7 h-7"
                style={{ filter: "brightness(0)" }}
              />
            </div>
            <span
              className="text-[9px] font-syne font-bold tracking-wide max-w-[75px] truncate"
              style={{ color: "#8b5cf6" }}
            >
              {user?.firstName
                ? `${user.firstName} ${user.lastName}`
                : "Account"}
            </span>
          </button>

          <button
            onClick={handleLogout}
            className="w-full flex flex-col items-center gap-2 py-3.5 px-2 transition-all"
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
            }}
            title="Logout"
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(239, 68, 68, 0.08)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
          >
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center"
              style={{ background: "rgba(239, 68, 68, 0.12)" }}
            >
              <img
                src={logoutIcon}
                alt="Logout"
                className="w-7 h-7"
                style={{ filter: "brightness(0)" }}
              />
            </div>
            <span
              className="text-[9px] font-syne font-bold tracking-wide"
              style={{ color: "#ef4444" }}
            >
              Logout
            </span>
          </button>
        </div>
      </div>

      {/* Centered Popup */}
      {activePopup && (
        <div
          className="fixed inset-0 z-[500] flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}
          onClick={() => {
            setActivePopup(null);
            resetPopupStates();
          }}
        >
          <div
            className="w-[50%] max-h-[80vh] bg-white shadow-2xl flex flex-col animate-pop-in rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0"
              style={{ borderColor: "#f3f4f6", background: "#ffffff" }}
            >
              <span
                className="text-lg font-extrabold"
                style={{ color: "#1f2937" }}
              >
                {getPopupTitle()}
              </span>
              <button
                onClick={() => {
                  setActivePopup(null);
                  resetPopupStates();
                }}
                className="w-8 h-8 rounded-lg flex items-center justify-center transition-all text-gray-500 text-xl font-bold"
                style={{
                  border: "1px solid #e5e7eb",
                  background: "#ffffff",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#fef2f2";
                  e.currentTarget.style.color = "#ef4444";
                  e.currentTarget.style.borderColor = "#ef4444";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#ffffff";
                  e.currentTarget.style.color = "#6b7280";
                  e.currentTarget.style.borderColor = "#e5e7eb";
                }}
              >
                ×
              </button>
            </div>
            <div
              className="flex-1 overflow-y-auto"
              style={{ background: "#ffffff" }}
            >
              {renderPopupContent()}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pop-in { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        .animate-pop-in { animation: pop-in 0.25s ease-out; }
        @keyframes check-circle-pop { 0% { transform: scale(0); opacity: 0; } 60% { transform: scale(1.1); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
        .check-circle { animation: check-circle-pop 0.6s ease-out forwards; }
        @keyframes checkmark-draw { 0% { opacity: 0; transform: scale(0) rotate(-10deg); } 60% { opacity: 1; transform: scale(1.3) rotate(5deg); } 100% { opacity: 1; transform: scale(1) rotate(0deg); } }
        .checkmark-animated { display: inline-block; font-size: 48px; font-weight: 900; color: #ffffff; animation: checkmark-draw 0.5s ease-out 0.3s forwards; opacity: 0; }
        @keyframes pulse-ring { 0% { transform: scale(1); opacity: 0.6; } 100% { transform: scale(1.6); opacity: 0; } }
        .pulse-ring { animation: pulse-ring 1s ease-out infinite; }
        @keyframes pulse-ring-delay { 0% { transform: scale(1); opacity: 0.5; } 100% { transform: scale(2); opacity: 0; } }
        .pulse-ring-delay { animation: pulse-ring-delay 1.5s ease-out 0.3s infinite; }
      `}</style>
    </>
  );
};

export default AppSidebar;